import React, { useState, useEffect } from 'react';
import { db, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { httpsCallable } from 'firebase/functions';
import {
  collection, query, onSnapshot, doc, getDocs, orderBy, collectionGroup, where
} from 'firebase/firestore';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Users, BookOpen, Megaphone, Star, TrendingUp, Shield, Search,
  Eye, Ban, ChevronUp, ChevronDown, BarChart3, CreditCard, UserPlus
} from 'lucide-react';

function MetricCard({ label, value, subtext, icon: Icon, color }) {
  return (
    <Card className="border-slate-100">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500">{label}</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
            {subtext && <p className="mt-0.5 text-xs text-slate-400">{subtext}</p>}
          </div>
          <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${color}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SuperAdminDashboard({ embedded = false }) {
  const { isSuperadmin } = useAuth();
  const [activeTab, setActiveTab] = useState('metrics');
  const [authors, setAuthors] = useState([]);
  const [allReaders, setAllReaders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedAuthor, setExpandedAuthor] = useState(null);
  const [showBookLimitDialog, setShowBookLimitDialog] = useState(null);
  const [newBookLimit, setNewBookLimit] = useState(5);

  // Load all authors
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'authors'), (snap) => {
      const items = [];
      snap.forEach(d => items.push({ id: d.id, ...d.data() }));
      setAuthors(items);
      setIsLoading(false);
    });
    return unsub;
  }, []);

  // Summary stats
  const totalAuthors = authors.length;
  const totalActiveBooks = authors.reduce((s, a) => s + (a.activeBookCount || 0), 0);
  const totalCampaigns = authors.reduce((s, a) => s + (a.activeCampaignCount || 0), 0);
  const freeAuthors = authors.filter(a => !a.tier || a.tier === 'free');
  const authorsAtLimit = authors.filter(a => {
    const limit = a.bookLimit || 5;
    return (a.activeBookCount || 0) >= limit;
  });

  async function handleUpdateBookLimit(authorId) {
    try {
      const fn = httpsCallable(functions, 'updateAuthorBookLimit');
      await fn({ authorId, bookLimit: Number(newBookLimit) });
      setShowBookLimitDialog(null);
    } catch (err) {
      console.error('Error updating limit:', err);
    }
  }

  async function handleSuspendAuthor(authorId, suspend) {
    try {
      const fn = httpsCallable(functions, 'suspendAuthor');
      await fn({ authorId, suspended: suspend });
    } catch (err) {
      console.error('Error suspending:', err);
    }
  }

  const filteredAuthors = authors.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (a.name || '').toLowerCase().includes(q) ||
      (a.penName || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q);
  });

  const TABS = [
    { id: 'metrics', label: 'Platform Metrics', icon: BarChart3 },
    { id: 'authors', label: 'Authors', icon: Users },
    { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
  ];

  const content = (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-teal-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Platform Administration</h1>
            <p className="text-sm text-slate-500">Manage authors, readers, and platform settings</p>
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon className="h-4 w-4" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Metrics Tab */}
      {activeTab === 'metrics' && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard label="Total Authors" value={totalAuthors} icon={UserPlus} color="bg-teal-100 text-teal-600" />
            <MetricCard label="Active Books" value={totalActiveBooks} icon={BookOpen} color="bg-blue-100 text-blue-600" />
            <MetricCard label="Active Campaigns" value={totalCampaigns} icon={Megaphone} color="bg-purple-100 text-purple-600" />
            <MetricCard
              label="Upgrade Candidates" value={authorsAtLimit.length}
              subtext="At book limit (free tier)"
              icon={TrendingUp} color="bg-green-100 text-green-600"
            />
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Author Tiers</h3>
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{freeAuthors.length}</p>
                  <p className="text-xs text-slate-500">Free</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {authors.filter(a => a.tier === 'pro').length}
                  </p>
                  <p className="text-xs text-slate-500">Pro</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {authors.filter(a => a.tier === 'publisher').length}
                  </p>
                  <p className="text-xs text-slate-500">Publisher</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Authors Tab */}
      {activeTab === 'authors' && (
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search authors..." />
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : filteredAuthors.length === 0 ? (
            <Card className="border-dashed border-slate-200">
              <CardContent className="p-8 text-center">
                <Users className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                <p className="text-sm text-slate-500">No authors found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredAuthors.map(author => (
                <Card key={author.id} className="border-slate-100">
                  <CardContent className="p-0">
                    <div className="flex items-center gap-4 p-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 font-medium text-teal-700">
                        {(author.penName || author.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="truncate font-medium text-slate-900">{author.name}</p>
                          {author.penName && <span className="text-xs text-slate-500">({author.penName})</span>}
                          {author.suspended && <Badge className="border-0 bg-red-100 text-red-700 text-[10px]">Suspended</Badge>}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>{author.email}</span>
                          <span>Joined {author.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <div className="text-center">
                          <p className="font-medium text-slate-900">{author.activeBookCount || 0}</p>
                          <p className="text-slate-500">Books</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium text-slate-900">{author.activeCampaignCount || 0}</p>
                          <p className="text-slate-500">Campaigns</p>
                        </div>
                        <Badge className={`border-0 text-[10px] ${
                          author.tier === 'pro' ? 'bg-blue-100 text-blue-700' :
                          author.tier === 'publisher' ? 'bg-purple-100 text-purple-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>{author.tier || 'free'}</Badge>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="text-xs"
                          onClick={() => { setShowBookLimitDialog(author.id); setNewBookLimit(author.bookLimit || 5); }}>
                          Limit
                        </Button>
                        <Button size="sm" variant="ghost" className="text-xs"
                          onClick={() => handleSuspendAuthor(author.id, !author.suspended)}>
                          {author.suspended ? 'Unsuspend' : 'Suspend'}
                        </Button>
                        <button
                          onClick={() => setExpandedAuthor(expandedAuthor === author.id ? null : author.id)}
                          className="rounded p-1 hover:bg-slate-100"
                        >
                          {expandedAuthor === author.id
                            ? <ChevronUp className="h-4 w-4 text-slate-400" />
                            : <ChevronDown className="h-4 w-4 text-slate-400" />}
                        </button>
                      </div>
                    </div>

                    {expandedAuthor === author.id && (
                      <div className="border-t border-slate-100 bg-slate-50/50 p-4">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="font-medium text-slate-700">Genres</p>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {(author.genres || []).map((g, i) => (
                                <Badge key={i} className="border-0 bg-slate-100 text-slate-600 text-[10px]">
                                  {g.label || g}
                                </Badge>
                              ))}
                              {(!author.genres || author.genres.length === 0) && (
                                <span className="text-xs text-slate-400">No genres set</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="font-medium text-slate-700">Book Limit</p>
                            <p className="mt-1 text-slate-600">{author.bookLimit || 5} active books</p>
                          </div>
                          <div>
                            <p className="font-medium text-slate-700">Social Links</p>
                            <div className="mt-1 text-xs text-slate-500">
                              {author.website && <p>{author.website}</p>}
                              {Object.entries(author.socialLinks || {}).filter(([, v]) => v).map(([k, v]) => (
                                <p key={k}>{k}: {v}</p>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="mb-2 text-sm font-semibold text-slate-700">Subscription Management</h3>
              <p className="text-sm text-slate-500">
                All authors are currently on the Free tier. When paid tiers are introduced,
                this is where you'll manage billing and upgrades.
              </p>
            </CardContent>
          </Card>
          <div className="space-y-2">
            {authors.map(author => (
              <Card key={author.id} className="border-slate-100">
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-sm font-medium text-slate-600">
                      {(author.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{author.name}</p>
                      <p className="text-xs text-slate-500">{author.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`border-0 ${
                      author.tier === 'pro' ? 'bg-blue-100 text-blue-700' :
                      author.tier === 'publisher' ? 'bg-purple-100 text-purple-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>{author.tier || 'Free'}</Badge>
                    <span className="text-xs text-slate-500">
                      {author.bookLimit || 5} book limit
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Book Limit Dialog */}
      <Dialog open={!!showBookLimitDialog} onOpenChange={() => setShowBookLimitDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Book Limit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Max Active Books</Label>
              <Input type="number" min="1" max="100" value={newBookLimit} onChange={e => setNewBookLimit(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBookLimitDialog(null)}>Cancel</Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white"
                onClick={() => handleUpdateBookLimit(showBookLimitDialog)}>
                Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-6xl">
        {content}
      </div>
    </div>
  );
}
